import { Gift } from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";

/** Max tilt (deg) while the pointer is over the card; ambient tilt elsewhere is a fraction. */
const MAX_TILT = 14;
const AMBIENT_TILT = 4;
/** How far scroll velocity can tip the card (deg) before it springs back. */
const MAX_SCROLL_TILT = 8;
/** Smoothing per frame (0–1): higher = snappier. */
const EASE = 0.12;

type Motion = {
  rx: number;
  ry: number;
  gx: number;
  gy: number;
  glare: number;
  sheen: number;
  /** 0..1 — hover lift. */
  lift: number;
};

/**
 * The /gift-cards preview as a brushed-titanium card: tilts toward the
 * pointer, tips with scroll momentum, and a specular glare follows the light.
 *
 * Motion is a rAF loop that writes CSS variables (no React re-renders) and
 * idles once settled. Falls back to a flat card for reduced motion or no
 * 3D-transform support.
 */
export const TitaniumGiftCard: React.FC<{
  amount: string;
  caption: string;
}> = ({ amount, caption }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const sweepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    const shadow = shadowRef.current;
    if (!card || !shadow) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const supports3d = CSS.supports("transform-style", "preserve-3d");
    if (reducedMotion || !supports3d) return;

    // One-time light sweep so the metal reads as metal before anyone moves.
    sweepRef.current?.animate(
      [
        { transform: "translateX(-120%) skewX(-20deg)", opacity: 0 },
        { opacity: 1, offset: 0.3 },
        { transform: "translateX(220%) skewX(-20deg)", opacity: 0 },
      ],
      { duration: 1600, delay: 400, easing: "cubic-bezier(0.4, 0, 0.2, 1)" },
    );

    const target: Motion = { rx: 0, ry: 0, gx: 50, gy: 30, glare: 0.25, sheen: 0, lift: 0 };
    const current: Motion = { ...target };
    let scrollTilt = 0;
    let lastScrollY = window.scrollY;
    let frame = 0;

    const apply = () => {
      const rx = current.rx + scrollTilt;
      card.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
      card.style.setProperty("--ry", `${current.ry.toFixed(2)}deg`);
      card.style.setProperty("--gx", `${current.gx.toFixed(1)}%`);
      card.style.setProperty("--gy", `${current.gy.toFixed(1)}%`);
      card.style.setProperty("--glare", current.glare.toFixed(3));
      card.style.setProperty("--lift", current.lift.toFixed(3));
      // The brushed sheen slides with the tilt like light across a real card.
      card.style.setProperty("--sheen", `${(50 + current.ry * 3 + current.sheen).toFixed(1)}%`);
      // Shadow falls away from the raised edge.
      shadow.style.transform = `translate3d(${(-current.ry * 1.2).toFixed(1)}px, ${(18 + rx * 1.2).toFixed(1)}px, 0) scale(0.92)`;
    };

    const tick = () => {
      let moving = false;
      for (const key of Object.keys(target) as Array<keyof Motion>) {
        const delta = target[key] - current[key];
        if (Math.abs(delta) > 0.01) {
          current[key] += delta * EASE;
          moving = true;
        } else {
          current[key] = target[key];
        }
      }
      // Scroll momentum springs back to level.
      scrollTilt *= 0.9;
      if (Math.abs(scrollTilt) > 0.01) moving = true;
      else scrollTilt = 0;

      apply();
      frame = moving ? requestAnimationFrame(tick) : 0;
    };

    const wake = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const rect = card.getBoundingClientRect();
      // -1..1 across the card; beyond its edges the influence fades out.
      const px = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const py = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      const over = Math.abs(px) <= 1 && Math.abs(py) <= 1;
      const clamp = (v: number) => Math.max(-1, Math.min(1, v));
      const strength = over ? MAX_TILT : AMBIENT_TILT;
      target.ry = clamp(px) * strength;
      target.rx = -clamp(py) * strength;
      target.gx = 50 + clamp(px) * 50;
      target.gy = 50 + clamp(py) * 50;
      target.glare = over ? 0.9 : 0.35;
      target.lift = over ? 1 : 0;
      wake();
    };

    const onPointerLeave = () => {
      target.rx = 0;
      target.ry = 0;
      target.gx = 50;
      target.gy = 30;
      target.glare = 0.35;
      target.lift = 0;
      wake();
    };

    const onScroll = () => {
      const delta = window.scrollY - lastScrollY;
      lastScrollY = window.scrollY;
      scrollTilt = Math.max(-MAX_SCROLL_TILT, Math.min(MAX_SCROLL_TILT, scrollTilt + delta * 0.08));
      target.sheen = (window.scrollY * 0.05) % 40;
      wake();
    };

    apply();
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div className="relative" style={{ perspective: "1100px" }}>
      {/* Contact shadow */}
      <div
        ref={shadowRef}
        aria-hidden
        className="absolute inset-x-6 bottom-0 h-1/2 rounded-[40%] bg-black/35 blur-2xl"
        style={{ transform: "translate3d(0, 18px, 0) scale(0.92)" }}
      />

      <div
        ref={cardRef}
        className="relative aspect-[1.6] rounded-2xl overflow-hidden text-white"
        style={
          {
            "--rx": "0deg",
            "--ry": "0deg",
            "--gx": "50%",
            "--gy": "30%",
            "--glare": "0.35",
            "--lift": "0",
            "--sheen": "50%",
            transform: "rotateX(var(--rx)) rotateY(var(--ry)) scale(calc(1 + var(--lift) * 0.03))",
            transformStyle: "preserve-3d",
            willChange: "transform",
            // Titanium: cool graphite with warmer mid-tones.
            background:
              "linear-gradient(135deg, #3d3f45 0%, #1c1d20 30%, #2d2e33 55%, #131416 80%, #28292d 100%)",
            // Machined bevel: bright top/left lip, dark bottom/right.
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.38), inset 1px 0 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.75), inset -1px 0 0 rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.35)",
          } as React.CSSProperties
        }
      >
        {/* Brushed grain */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-60 mix-blend-overlay"
          style={{
            background:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px), repeating-linear-gradient(90deg, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 1px, transparent 1px, transparent 7px)",
          }}
        />
        {/* Anisotropic sheen — the long highlight brushed metal throws */}
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-screen"
          style={{
            background:
              "linear-gradient(100deg, transparent 25%, rgba(255,255,255,0.04) 38%, rgba(225,235,245,0.32) 48%, rgba(255,255,255,0.04) 58%, transparent 72%)",
            backgroundSize: "250% 100%",
            backgroundPosition: "var(--sheen) 0",
          }}
        />
        {/* Faint iridescence, as anodized titanium does at an angle */}
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-overlay opacity-[0.14]"
          style={{
            background:
              "linear-gradient(115deg, #94e7ce 0%, #8fb7ff 28%, #d8a8ff 52%, #ffd9a0 76%, #94e7ce 100%)",
            backgroundSize: "300% 100%",
            backgroundPosition: "var(--sheen) 0",
          }}
        />
        {/* Specular glare following the light */}
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-screen"
          style={{
            background:
              "radial-gradient(circle at var(--gx) var(--gy), rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.08) 20%, transparent 48%)",
            opacity: "var(--glare)",
          }}
        />
        {/* Intro light sweep (Web Animations API) */}
        <div
          ref={sweepRef}
          aria-hidden
          className="absolute inset-y-0 w-1/3 opacity-0"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
            transform: "translateX(-120%) skewX(-20deg)",
          }}
        />

        {/* Engraved content, lifted off the surface */}
        <div
          className="relative h-full p-6 flex flex-col justify-between"
          style={{ transform: "translateZ(40px)", transformStyle: "preserve-3d" }}
        >
          <div className="flex items-center justify-between">
            <span
              className="text-sm font-semibold tracking-[0.18em] uppercase text-[#e4e6ea]"
              style={{
                textShadow: "0 1px 0 rgba(0,0,0,0.65), 0 -1px 0 rgba(255,255,255,0.12)",
              }}
            >
              Visionary Meals
            </span>
            <Gift className="size-5 text-brand-mint drop-shadow-[0_1px_0_rgba(0,0,0,0.6)]" />
          </div>
          <div>
            <p
              className="text-4xl font-bold bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, #e6fff8 0%, #94e7ce 45%, #4fc9a6 70%, #b8f5e3 100%)",
                filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.55))",
              }}
            >
              {amount}
            </p>
            <p
              className="text-xs text-white/75 mt-1 truncate"
              style={{ textShadow: "0 1px 0 rgba(0,0,0,0.6)" }}
            >
              {caption}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
